// CompletenessBanner
// Shown at the top of the Matches feed when the user's onboarding data is
// incomplete. The feed is ranked from that data, so the prompt is framed as
// what they get back — better matches — rather than as an unfinished chore.

import React, { useCallback, useEffect, useState } from 'react';
import { profileService, ProfileCompleteness } from '../../../services/Profile/profileService';
import { OnboardingModal } from '../../onboarding/OnboardingModal';
import { OnboardingStepId } from '../../../types/onboarding';
import { logger } from '../../../utils/logger';
import {
  BannerContainer,
  BannerTopRow,
  BannerTitle,
  BannerSubtitle,
  BannerDismiss,
  ProgressTrack,
  ProgressFill,
  MissingList,
  MissingRow,
  MissingLabel,
  MissingWhy,
  MissingGain,
} from '../../../styles/SocialFeed/CompletenessBanner.styles';

// Dismissal is per-session only: the feed keeps getting worse until the data
// is filled in, so this shouldn't be permanently silenceable.
const DISMISS_KEY = 'poh:feed-completeness-dismissed';

// At most two asks at a time — a seven-item checklist reads as work.
const MAX_VISIBLE_ITEMS = 2;

interface CompletenessBannerProps {
  /** Rendered inline in the empty state instead of pinned over the feed. */
  variant?: 'pinned' | 'inline';
  onLoaded?: (completeness: ProfileCompleteness) => void;
  /**
   * Fired after the setup flow closes, once the new completeness is in.
   * The host should refetch the feed here: the whole promise of this banner
   * is "fill this in and your matches improve", and the cards behind it were
   * ranked from the old data. Reloading only the banner made the claim
   * unverifiable — the score moved, the people did not.
   */
  onProfileUpdated?: () => void;
}

export const CompletenessBanner: React.FC<CompletenessBannerProps> = ({
  variant = 'pinned',
  onLoaded,
  onProfileUpdated,
}) => {
  const [completeness, setCompleteness] = useState<ProfileCompleteness | null>(null);
  const [openStep, setOpenStep] = useState<OnboardingStepId | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  const loadCompleteness = useCallback(async () => {
    try {
      const data = await profileService.getCompleteness();
      setCompleteness(data);
      onLoaded?.(data);
      return data;
    } catch (err) {
      logger.error('Failed to load profile completeness:', err);
      return null;
    }
  }, [onLoaded]);

  useEffect(() => {
    loadCompleteness();
  }, [loadCompleteness]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  // Re-read from the server after the flow closes. This is what makes the
  // prompt honest: whatever the user just filled in is reflected immediately,
  // and the banner removes itself the moment the score reaches 100%.
  const handleModalClose = useCallback(async () => {
    setOpenStep(null);
    await loadCompleteness();
    // Ordered deliberately: the save has already happened inside the modal,
    // and completeness has been re-read, so by the time the feed refetches it
    // is ranking on the new answers rather than racing them.
    onProfileUpdated?.();
  }, [loadCompleteness, onProfileUpdated]);

  // The modal must outlive the banner's own visibility: finishing the last
  // item makes `isComplete` true, and if the banner returned null first the
  // modal would be unmounted mid-close.
  const modal = (
    <OnboardingModal
      isOpen={openStep !== null}
      initialStepId={openStep ?? undefined}
      onClose={handleModalClose}
    />
  );

  if (!completeness || completeness.isComplete) return modal;
  if (dismissed && variant === 'pinned') return modal;

  const visible = completeness.missing.slice(0, MAX_VISIBLE_ITEMS);
  const remaining = completeness.missing.length - visible.length;

  return (
    <>
    {modal}
    <BannerContainer
      style={variant === 'inline' ? { position: 'static', margin: 0 } : undefined}
    >
      <BannerTopRow>
        <div>
          <BannerTitle>
            Your matches are running at {completeness.score}%
          </BannerTitle>
          <BannerSubtitle>
            We rank your feed from your onboarding answers — fill in the gaps and
            the people you see get noticeably closer to your type.
          </BannerSubtitle>
        </div>
        {variant === 'pinned' && (
          <BannerDismiss onClick={handleDismiss} aria-label="Dismiss for now">
            ×
          </BannerDismiss>
        )}
      </BannerTopRow>

      <ProgressTrack>
        <ProgressFill $percent={completeness.score} />
      </ProgressTrack>

      <MissingList>
        {visible.map((item) => (
          // Opens the onboarding flow on the step that collects this field.
          <MissingRow key={item.key} onClick={() => setOpenStep(item.step)}>
            <span>
              <MissingLabel>{item.label}</MissingLabel>
              <MissingWhy>{item.why}</MissingWhy>
            </span>
            <MissingGain>+{item.weight}%</MissingGain>
          </MissingRow>
        ))}
      </MissingList>

      {remaining > 0 && (
        <BannerSubtitle>
          {remaining} smaller {remaining === 1 ? 'item' : 'items'} left after that.
        </BannerSubtitle>
      )}
    </BannerContainer>
    </>
  );
};
